import { useEffect, useRef, useCallback, useState } from 'react';
import { useEditorStore, useCreativeStore, useTemplateStore, useAnimationStore } from '@/stores';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface AutoSaveOptions {
  debounceMs?: number;
  enabled?: boolean;
}

interface AutoSaveResult {
  status: SaveStatus;
  lastSaved: Date | null;
  save: () => Promise<void>;
  error: string | null;
}

export function useAutoSave(options: AutoSaveOptions = {}): AutoSaveResult {
  const { debounceMs = 2000, enabled = true } = options;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { currentCreativeId, updateCreative } = useCreativeStore();
  const { selectedFormatId, content } = useEditorStore();
  const { currentTemplate, variableValues } = useTemplateStore();
  const { timeline } = useAnimationStore();

  // Create a stable reference to track if content actually changed
  const lastSavedDataRef = useRef<string>('');

  const save = useCallback(async () => {
    if (!currentCreativeId || !currentTemplate) return;

    // Create a hash of current data to check if anything changed
    const currentData = JSON.stringify({
      content,
      templateData: currentTemplate,
      timelineData: timeline,
      variableValues,
    });

    // Skip if nothing changed since last save
    if (currentData === lastSavedDataRef.current) {
      setStatus('saved');
      return;
    }

    setStatus('saving');
    setError(null);

    try {
      await updateCreative(currentCreativeId, {
        primaryFormatId: selectedFormatId,
        content,
        templateData: JSON.stringify(currentTemplate),
        timelineData: timeline ? JSON.stringify(timeline) : undefined,
        variableValues,
      });

      lastSavedDataRef.current = currentData;
      setLastSaved(new Date());
      setStatus('saved');

      // Reset to idle after a short delay
      setTimeout(() => setStatus('idle'), 2000);
    } catch (e) {
      setError((e as Error).message);
      setStatus('error');
    }
  }, [
    currentCreativeId,
    currentTemplate,
    timeline,
    variableValues,
    selectedFormatId,
    content,
    updateCreative,
  ]);

  // Debounced auto-save triggered by changes
  useEffect(() => {
    if (!enabled || !currentCreativeId || !currentTemplate) return;

    // Mark as pending when changes detected
    setStatus('pending');

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      save();
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    enabled,
    currentCreativeId,
    currentTemplate,
    timeline,
    variableValues,
    selectedFormatId,
    content,
    debounceMs,
    save,
  ]);

  // Reset state when creative changes
  useEffect(() => {
    setStatus('idle');
    setError(null);
    lastSavedDataRef.current = '';
  }, [currentCreativeId]);

  return { status, lastSaved, save, error };
}
